import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeButton } from '@/components/ui/tee-button';
import TeeBadge from '@/components/ui/tee-badge';
import { LocationSwitcher } from '@/components/organisms/LocationSwitcher/LocationSwitcher';
import { useGamePlanLocation } from '@/hooks/useGamePlanLocation';
import { useGamePlanOverview, useGoalBoard } from '@/hooks/useGamePlanTracker';
import { OnboardingWizard } from './components/OnboardingWizard';
import { AdjustPlanButton } from './components/AdjustPlanButton';
import type {
  GoalBoard,
  GoalBoardChannel,
  PaceStatus,
} from '@/types/gamePlanTracker';

const paceBadge: Record<PaceStatus, 'grey' | 'green' | 'amber'> = {
  start: 'grey',
  ahead: 'green',
  on: 'green',
  catch: 'amber',
};
const micro =
  'text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]';

export default function GoalBoardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locations, locationId, setLocationId, currentLocation } =
    useGamePlanLocation();

  const { data: overview, isLoading: ovLoading, refetch } =
    useGamePlanOverview(locationId);
  const onboarded = overview?.onboarded ?? false;
  const { data: board, isLoading } = useGoalBoard(
    onboarded ? locationId : undefined,
  );
  const switcher =
    locations && locations.length > 1 && locationId ? (
      <LocationSwitcher
        currentLocationId={locationId}
        currentLocationName={currentLocation?.name ?? ''}
        onSelect={setLocationId}
      />
    ) : undefined;

  return (
    <div className="section_container">
      {switcher && <div className="flex justify-end mb-3">{switcher}</div>}
      {!locationId || ovLoading ? (
        <TeeLoader size="md" text={t('common.loading', 'Loading…')} />
      ) : !onboarded ? (
        <OnboardingWizard
          locationId={locationId}
          defaultGoal={overview?.defaults?.foundingMemberGoal ?? 50}
          defaultDate={overview?.defaults?.grandOpeningDate ?? ''}
          onComplete={() => refetch()}
        />
      ) : isLoading || !board ? (
        <TeeLoader size="md" text={t('common.loading', 'Loading…')} />
      ) : (
        <div className="flex flex-col gap-7">
          <KpiRow
            board={board}
            locationName={currentLocation?.name}
            adjust={<AdjustPlanButton locationId={locationId} tone="forest" />}
          />
          <RequiredPace board={board} />
          <Channels board={board} />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-7">
            <Trend board={board} />
            <Projection board={board} />
          </div>
          <PhaseTargets board={board} />
          <div className="flex flex-wrap gap-3 pt-1">
            <TeeButton
              variant="primary"
              size="m"
              text="Act on this in This Week →"
              onClick={() => navigate('/game-plan/current-week')}
            />
            <TeeButton
              variant="secondary"
              size="m"
              text="See the 16-week Roadmap →"
              onClick={() => navigate('/game-plan/overview')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function KpiRow({
  board,
  locationName,
  adjust,
}: {
  board: GoalBoard;
  locationName?: string;
  adjust?: React.ReactNode;
}) {
  const pct = Math.min(100, Math.round((board.members / board.goal) * 100));
  return (
    <div className="rounded-[12px] bg-[var(--color-forest)] text-[var(--text-on-forest)] p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gold-500)]">
          Goal Board{locationName ? ` · ${locationName}` : ''}
        </div>
        {adjust}
      </div>
      <div className="flex flex-wrap items-center gap-6">
      <div className="flex-1 min-w-[180px]">
        <div className={micro} style={{ color: 'var(--text-on-forest-muted)' }}>
          Founding members
        </div>
        <div className="text-[30px] font-bold">
          {board.members}
          <span className="text-[16px] text-[var(--text-on-forest-muted)]">
            {' '}
            / {board.goal}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[rgba(251,246,236,0.18)] mt-2 max-w-[260px]">
          <div
            className="h-1.5 rounded-full bg-[var(--color-gold-500)]"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <HeroFig label="Leads" value={String(board.leads)} />
      <HeroFig label="Lead → member" value={`${board.conversionPct}%`} />
      <div className="flex flex-col items-start gap-1.5 min-w-[160px]">
        <span className={micro} style={{ color: 'var(--text-on-forest-muted)' }}>
          Pace
        </span>
        <TeeBadge label={board.pace.label} type={paceBadge[board.pace.status]} size="m" />
        <p className="text-[12px] text-[var(--text-on-forest-muted)] max-w-[220px]">
          {board.pace.narrative}
        </p>
      </div>
      </div>
    </div>
  );
}

function HeroFig({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-[90px]">
      <div className={micro} style={{ color: 'var(--text-on-forest-muted)' }}>
        {label}
      </div>
      <div className="text-[30px] font-bold">{value}</div>
    </div>
  );
}

function RequiredPace({ board }: { board: GoalBoard }) {
  return (
    <div className="rounded-[10px] bg-[var(--background-page-header)] p-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="text-heading-h5 text-[var(--text-content-title)]">
        {board.neededPace} leads/week
      </span>
      <span className="text-body-s text-[var(--text-content-sub-title)]">
        keeps you on track — {board.leadsStillNeeded} more leads needed to reach{' '}
        {board.goal} founding members.
      </span>
    </div>
  );
}

function Channels({ board }: { board: GoalBoard }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className={micro}>Channel mix</span>
        <span className="text-[12px] text-[var(--text-content-sub-title)]">
          weekly target · to date
        </span>
      </div>
      <div className="flex flex-col">
        {board.channels.map((c) => (
          <ChannelRow key={c.key} c={c} />
        ))}
      </div>
    </div>
  );
}

function ChannelRow({ c }: { c: GoalBoardChannel }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-[var(--border-default)] last:border-b-0">
      <div className="flex-1">
        <div className="text-body-m text-[var(--text-content-title)]">{c.label}</div>
        <div className="text-[12px] text-[var(--text-content-sub-title)]">{c.focus}</div>
      </div>
      <div className="text-center w-[90px]">
        <div className={micro}>Weekly</div>
        <div
          className={
            'text-body-m font-semibold ' +
            (c.weekMet
              ? 'text-[var(--color-forest)]'
              : 'text-[var(--text-content-title)]')
          }
        >
          {c.thisWeek}
          <span className="text-[12px] text-[var(--text-content-sub-title)]">
            {' '}
            / {c.targetLabel}
          </span>
        </div>
      </div>
      <div className="text-center w-[90px]">
        <div className={micro}>To date</div>
        <div
          className={
            'text-body-m font-semibold ' +
            (c.toDateMet
              ? 'text-[var(--color-forest)]'
              : 'text-[var(--color-text-gold)]')
          }
        >
          {c.toDate}
          <span className="text-[12px] text-[var(--text-content-sub-title)]">
            {' '}
            / {c.targetToDate}
          </span>
        </div>
      </div>
    </div>
  );
}

function Trend({ board }: { board: GoalBoard }) {
  const { points, goal } = board.trend;
  const scale = (v: number) => Math.min(100, (v / goal) * 100);
  const expLine = points
    .map((p) => `${(p.week - 1) * 10 + 3},${100 - scale(p.expected)}`)
    .join(' ');
  return (
    <div>
      <span className={micro}>Members vs. expected pace</span>
      <div className="mt-3 rounded-[10px] border border-[var(--border-default)] p-4">
        <svg viewBox="0 0 160 110" className="w-full h-[180px]" preserveAspectRatio="none">
          {/* actual member bars */}
          {points.map((p) =>
            p.members != null ? (
              <rect
                key={p.week}
                x={(p.week - 1) * 10 + 1.5}
                y={100 - Math.max(2, scale(p.members))}
                width={5}
                height={Math.max(2, scale(p.members))}
                rx={1}
                fill="var(--color-forest)"
              />
            ) : null,
          )}
          {/* expected-pace line */}
          <polyline
            points={expLine}
            fill="none"
            stroke="var(--color-gold-500)"
            strokeWidth={1.5}
            strokeDasharray="3 2"
          />
        </svg>
        <div className="flex items-center gap-4 mt-2 text-[12px] text-[var(--text-content-sub-title)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-[2px] bg-[var(--color-forest)]" /> Actual
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 border-t-2 border-dashed border-[var(--color-gold-500)]" />{' '}
            Expected
          </span>
        </div>
      </div>
    </div>
  );
}

function Projection({ board }: { board: GoalBoard }) {
  return (
    <div>
      <span className={micro}>Projected at opening</span>
      <div className="mt-3 rounded-[10px] bg-[var(--background-page-header)] p-4">
        <div className="text-[34px] font-bold text-[var(--text-content-title)]">
          {board.projection.value ?? '—'}
        </div>
        <p className="text-body-s text-[var(--text-content-sub-title)] mt-1">
          {board.projection.note}
        </p>
      </div>
    </div>
  );
}

function PhaseTargets({ board }: { board: GoalBoard }) {
  return (
    <div>
      <span className={micro}>Member targets by phase</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
        {board.phaseTargets.map((p) => (
          <div
            key={p.phase}
            className="rounded-[10px] border border-[var(--border-default)] p-3"
          >
            <div className="text-[12px] text-[var(--text-content-sub-title)]">
              Phase {p.phase}
            </div>
            <div className="text-heading-h5 text-[var(--text-content-title)]">
              {p.target}
            </div>
            <div className="text-[12px] text-[var(--text-content-sub-title)] leading-tight mt-0.5">
              {p.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
